import { useMemo, useState } from "react";
import { Alert } from "@volca/ui/components/ui/alert";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import { LevelSelect } from "@/payroll/workbook-access/level-select";
import { OptionCheckbox } from "@/payroll/workbook-access/option-checkbox";
import {
  fromPersonAccessForm,
  type PersonAccessMode,
  toPersonAccessForm,
} from "@/payroll/workbook-access/person-access-form";
import type {
  AccessAssignment,
  AccessPerson,
  WorkbookAccessSettings,
  WorkingAccess,
} from "@/payroll/workbook-access/types";
import {
  areaName,
  assignmentsFor,
  replacePersonAssignments,
  sameWorking,
} from "@/payroll/workbook-access/utils";
import { AccessModeOptions } from "./access-mode-options";
import { AreaGrantRow } from "./area-grant-row";
import { LockedAccessSummary } from "./locked-access-summary";
import { PersonAccessCheck } from "./person-access-check";

export interface PersonAccessEditorProps {
  businessId: string;
  person: AccessPerson;
  settings: WorkbookAccessSettings;
  working: WorkingAccess;
  onSave: (rows: AccessAssignment[]) => void;
  onCancel: () => void;
  onTransfer: () => void;
  saving: boolean;
  embedded?: boolean;
  saveLabel?: string;
}

export const PersonAccessEditor = ({
  businessId,
  person,
  settings,
  working,
  onSave,
  onCancel,
  onTransfer,
  saving,
  embedded = false,
  saveLabel,
}: PersonAccessEditorProps) => {
  const areas = settings.access_keys;
  const previous = useMemo(
    () => assignmentsFor(working.assignments, person.id),
    [working.assignments, person.id],
  );
  const [form, setForm] = useState(() => toPersonAccessForm(previous, areas));
  // The API refuses any edit to a removed person's grants: they may be kept
  // exactly as held or taken away.
  const allowedModes = useMemo(() => {
    if (!person.removed) return undefined;
    const held = toPersonAccessForm(previous, areas).mode;
    return Array.from(new Set<PersonAccessMode>(["none", held]));
  }, [person.removed, previous, areas]);
  const rows = fromPersonAccessForm(person.id, form, previous, areas);
  const candidate = replacePersonAssignments(working, person.id, rows);
  const baseline = replacePersonAssignments(
    working,
    person.id,
    fromPersonAccessForm(
      person.id,
      toPersonAccessForm(previous, areas),
      previous,
      areas,
    ),
  );
  const hasChanges = !sameWorking(baseline, candidate);
  const isSelf = person.id === settings.viewer.user_id;
  const lastOwner = person.access_locked;
  const locked = person.removed;
  const teamKeys = new Set(
    working.keySettings
      .filter((key) => key.team_eligible)
      .map((key) => key.key),
  );
  const notifyUnavailableReason = (key: string | null) => {
    if (!person.can_answer_pay_questions)
      return "This role cannot answer technicians' pay questions.";
    if (key && teamKeys.size > 0 && !teamKeys.has(key))
      return "Pay-question emails require a sheet enabled for teams. Use Sheets and teams to enable this sheet, or choose Whole workbook access.";
  };
  // An existing live email flag can be retained even when it is no longer
  // effective. Match the API: block new flags, but always allow clearing them.
  const invalidNotify = rows.some(
    (row) =>
      row.notify_pack_questions &&
      notifyUnavailableReason(row.access_key) &&
      !settings.live.assignments.some(
        (live) =>
          live.user_id === person.id &&
          live.full_workbook_access === row.full_workbook_access &&
          live.access_key === row.access_key &&
          live.notify_pack_questions,
      ),
  );

  const saveButton = (
    <Button
      disabled={saving || !hasChanges || invalidNotify}
      loading={saving}
      onClick={() => {
        if (hasChanges && !invalidNotify) onSave(rows);
      }}
    >
      {saveLabel ?? (embedded ? "Save access" : "Apply")}
    </Button>
  );
  const cancelButton = (
    <Button variant="outline" disabled={saving} onClick={onCancel}>
      Cancel
    </Button>
  );
  return (
    <Stack className={cn("min-h-0 w-full", { "flex-1": !embedded })} gap={4}>
      <Stack
        align="start"
        gap={4}
        className={cn("w-full", { "flex-1 overflow-y-auto p-6": !embedded })}
      >
        {lastOwner ? (
          <LockedAccessSummary
            name={person.name}
            isSelf={isSelf}
            onTransfer={onTransfer}
            rows={previous}
            keySettings={working.keySettings}
            areas={areas}
          />
        ) : (
          <>
            {person.removed && (
              <Text variant="caption" tone="muted">
                This person has been removed. Their access can be kept as it is
                or taken away, but not changed or added to.
              </Text>
            )}
            <Stack className="w-full" align="start" gap={1}>
              <Text variant="label">Access</Text>
              <AccessModeOptions
                value={form.mode}
                allowed={allowedModes}
                onChange={(mode) => setForm({ ...form, mode })}
              />
            </Stack>

            {form.mode === "full" && (
              <Stack className="w-full" align="start" gap={2}>
                <Stack className="w-full" align="start" gap={1}>
                  <Text variant="label">Access level</Text>
                  <LevelSelect
                    value={form.fullLevel}
                    label="Access level for the whole workbook"
                    disabled={locked}
                    onChange={(fullLevel) => setForm({ ...form, fullLevel })}
                  />
                </Stack>
                <OptionCheckbox
                  id="full-notify"
                  label="Emailed technicians' pay questions"
                  hint={notifyUnavailableReason(null)}
                  checked={form.fullNotify}
                  disabled={
                    locked ||
                    (!person.can_answer_pay_questions && !form.fullNotify)
                  }
                  onCheckedChange={(fullNotify) =>
                    setForm({ ...form, fullNotify })
                  }
                />
              </Stack>
            )}

            {form.mode !== "none" && areas.length > 0 && (
              <Stack className="w-full" align="start" gap={2}>
                <Stack align="start" gap={0}>
                  <Text variant="label">
                    {form.mode === "full" ? "Sign-off" : "Sheets"}
                  </Text>
                  <Text variant="caption" tone="muted">
                    {form.mode === "full"
                      ? "They already see every tab. Pick the sheets they approve."
                      : "Pick the sheets they can open, then how."}
                  </Text>
                </Stack>
                <Stack align="start" gap={2} className="w-full">
                  {areas.map((area) => (
                    <AreaGrantRow
                      key={area.key}
                      areaKey={area.key}
                      name={areaName(area.key, working.keySettings, areas)}
                      sheets={area.sheets}
                      grant={form.areas[area.key]}
                      approvalOnly={form.mode === "full"}
                      notifyUnavailableReason={notifyUnavailableReason(
                        area.key,
                      )}
                      locked={locked}
                      onChange={(grant) =>
                        setForm({
                          ...form,
                          areas: { ...form.areas, [area.key]: grant },
                        })
                      }
                    />
                  ))}
                </Stack>
              </Stack>
            )}

            <PersonAccessCheck
              businessId={businessId}
              userId={person.id}
              candidate={candidate}
            />
            {invalidNotify && (
              <Alert color="danger">
                Clear the unavailable pay-question email option before saving.
              </Alert>
            )}
          </>
        )}
      </Stack>

      {!lastOwner && (
        <Stack
          align="start"
          direction="row"
          justify={embedded ? "start" : "end"}
          gap={2}
          className={cn("w-full", { "border-t border-line p-6": !embedded })}
        >
          {embedded ? saveButton : cancelButton}
          {embedded ? cancelButton : saveButton}
        </Stack>
      )}
    </Stack>
  );
};
